import * as Sentry from "@sentry/tanstackstart-react";
import { createMiddleware } from "@tanstack/react-start";
import { isExpectedOutcome } from "~/lib/expected-errors";

/** Sentry's span status for "the thing asked for does not exist". */
const NOT_FOUND_STATUS = { code: 2, message: "not_found" } as const;

/**
 * Reports `error` to Sentry unless it is an expected outcome (a stale preview
 * link): that is a 404, not a fault. The span is marked "not found" instead of
 * being left to the "internal error" a throwing span gets by default.
 */
function reportError(error: unknown, span: Sentry.Span) {
  if (isExpectedOutcome(error)) {
    span.setStatus(NOT_FOUND_STATUS);
    return;
  }
  Sentry.captureException(error);
}

export const sentryMiddleware = createMiddleware().server(({ next }) =>
  Sentry.startSpan(
    { name: "server-function", op: "function" },
    async (span) => {
      try {
        return await next();
      } catch (error) {
        reportError(error, span);
        throw error;
      }
    }
  )
);

/**
 * Wraps an async function with Sentry tracing and error capture.
 * Use this for codepaths that cannot use the middleware chain
 * (e.g., TanStack AI tool servers, raw route handlers).
 */
export function withSentry<T>(name: string, fn: () => Promise<T>): Promise<T> {
  return Sentry.startSpan({ name, op: "function" }, async (span) => {
    try {
      return await fn();
    } catch (error) {
      reportError(error, span);
      throw error;
    }
  });
}
